import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { Helmet } from 'react-helmet-async';

export default function PrivacyPolicyPage() {
  return (
    <main data-testid="privacy-policy-page" className="pt-24">
      <Helmet>
        <title>Privatlivspolitik | Dilling El</title>
        <meta name="description" content="Læs Dilling El's privatlivs- og cookiepolitik. Information om behandling af personoplysninger, brug af cookies og dine rettigheder efter GDPR." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://dillingel.dk/privatlivspolitik" />
      </Helmet>

      {/* Hero */}
      <section className="py-16 md:py-24 bg-[#F4F2EE]">
        <div className="max-w-[900px] mx-auto px-8 md:px-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Link 
              to="/"
              className="inline-flex items-center gap-2 text-[#A6B8A2] hover:text-[#0E3D24] transition-colors mb-8"
            >
              <ArrowLeft size={16} />
              <span className="text-sm">Tilbage til forsiden</span>
            </Link>
            <h1 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-medium text-[#0E3D24] leading-[1.1]">
              Privatlivs- og cookiepolitik
            </h1>
            <p className="text-[#1E2A24]/50 text-sm mt-4">
              Senest opdateret: September 2026
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-[900px] mx-auto px-8 md:px-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="prose prose-lg max-w-none"
          >
            {/* Introduction */}
            <div className="mb-12">
              <p className="text-[#1E2A24]/70 leading-relaxed">
                Hos Dilling El ApS tager vi din privatlivsbeskyttelse alvorligt. Denne politik beskriver, 
                hvordan vi indsamler, bruger og beskytter dine personoplysninger, når du besøger vores 
                hjemmeside og benytter vores tjenester.
              </p>
            </div>

            {/* Data Controller */}
            <div className="mb-12">
              <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#0E3D24] mb-4">
                1. Dataansvarlig
              </h2>
              <p className="text-[#1E2A24]/70 leading-relaxed mb-4">
                Den dataansvarlige for behandling af personoplysninger på denne hjemmeside er:
              </p>
              <div className="bg-[#F4F2EE] p-6 text-[#1E2A24]/70">
                <p className="font-medium text-[#0E3D24]">Dilling El ApS</p>
                <p>Gammel Darupvej 5D</p>
                <p>4000 Roskilde</p>
                <p>CVR: 39987465</p>
                <p className="mt-2">
                  Email: <a href="mailto:info@dilling-el.dk" className="text-[#1F5A3A] hover:text-[#0E3D24]">info@dilling-el.dk</a>
                </p>
                <p>
                  Telefon: <a href="tel:+4546461234" className="text-[#1F5A3A] hover:text-[#0E3D24]">+45 46 46 12 34</a>
                </p>
              </div>
            </div>

            {/* Personal Data Collection */}
            <div className="mb-12">
              <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#0E3D24] mb-4">
                2. Personoplysninger vi indsamler
              </h2>
              
              <h3 className="font-medium text-[#0E3D24] mt-6 mb-3">Via kontaktformularen</h3>
              <p className="text-[#1E2A24]/70 leading-relaxed mb-4">
                Når du udfylder kontaktformularen på vores hjemmeside, indsamler vi følgende oplysninger:
              </p>
              <ul className="list-disc pl-6 text-[#1E2A24]/70 space-y-2">
                <li><strong>Navn</strong> – for at kunne tiltale dig personligt</li>
                <li><strong>Email-adresse</strong> – for at kunne besvare din henvendelse</li>
                <li><strong>Telefonnummer</strong> (valgfrit) – for at kunne kontakte dig telefonisk</li>
                <li><strong>Virksomhedsnavn</strong> (valgfrit) – for at forstå din forretningsmæssige kontekst</li>
                <li><strong>Din besked</strong> – indholdet af din henvendelse</li>
              </ul>

              <h3 className="font-medium text-[#0E3D24] mt-6 mb-3">Via Google Analytics</h3>
              <p className="text-[#1E2A24]/70 leading-relaxed">
                Hvis du giver samtykke til analytics-cookies, indsamler vi anonymiserede oplysninger om 
                din brug af hjemmesiden, herunder besøgte sider, tidspunkt for besøg og generel 
                brugeradfærd. Disse data er anonyme og kan ikke bruges til at identificere dig personligt.
              </p>
            </div>

            {/* Purpose of Processing */}
            <div className="mb-12">
              <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#0E3D24] mb-4">
                3. Formål med behandlingen
              </h2>
              <p className="text-[#1E2A24]/70 leading-relaxed mb-4">
                Vi behandler dine personoplysninger til følgende formål:
              </p>
              <ul className="list-disc pl-6 text-[#1E2A24]/70 space-y-2">
                <li><strong>Besvare henvendelser</strong> – når du kontakter os via kontaktformularen, bruger vi dine oplysninger til at behandle og besvare din henvendelse</li>
                <li><strong>Forbedre hjemmesiden</strong> – analytics-data hjælper os med at forstå, hvordan besøgende bruger siden, så vi kan optimere brugeroplevelsen</li>
                <li><strong>Overholde lovkrav</strong> – i visse tilfælde kan vi være forpligtet til at opbevare data af juridiske eller regulatoriske årsager</li>
              </ul>
            </div>

            {/* Legal Basis */}
            <div className="mb-12">
              <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#0E3D24] mb-4">
                4. Retsgrundlag
              </h2>
              <p className="text-[#1E2A24]/70 leading-relaxed mb-4">
                Vores behandling af personoplysninger sker på følgende retsgrundlag:
              </p>
              <ul className="list-disc pl-6 text-[#1E2A24]/70 space-y-2">
                <li><strong>Samtykke</strong> (GDPR artikel 6, stk. 1, litra a) – for analytics-cookies</li>
                <li><strong>Legitime interesser</strong> (GDPR artikel 6, stk. 1, litra f) – for at besvare henvendelser og forbedre vores tjenester</li>
                <li><strong>Opfyldelse af kontrakt</strong> (GDPR artikel 6, stk. 1, litra b) – hvis din henvendelse fører til et samarbejde</li>
              </ul>
            </div>

            {/* Cookies */}
            <div className="mb-12">
              <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#0E3D24] mb-4">
                5. Cookies og lignende teknologier
              </h2>
              
              <h3 className="font-medium text-[#0E3D24] mt-6 mb-3">5.1 Nødvendige cookies og localStorage</h3>
              <p className="text-[#1E2A24]/70 leading-relaxed mb-4">
                Vores hjemmeside bruger localStorage til at gemme dit cookievalg. Dette er nødvendigt 
                for at huske, om du har accepteret eller afvist analytics-cookies, så du ikke bliver 
                spurgt ved hvert besøg.
              </p>
              <div className="bg-[#F4F2EE] p-4 text-sm text-[#1E2A24]/70 mb-4">
                <p><strong>Navn:</strong> cookie_consent</p>
                <p><strong>Type:</strong> localStorage</p>
                <p><strong>Formål:</strong> Gemmer dit cookievalg ("accepted" eller "rejected")</p>
                <p><strong>Udløb:</strong> Slettes ikke automatisk – kan fjernes manuelt i browserindstillinger</p>
              </div>

              <h3 className="font-medium text-[#0E3D24] mt-6 mb-3">5.2 Google Analytics 4 (analytics-cookies)</h3>
              <p className="text-[#1E2A24]/70 leading-relaxed mb-4">
                Vi bruger Google Analytics 4 til at analysere trafikken på vores hjemmeside. 
                Google Analytics sætter cookies, der hjælper os med at forstå, hvordan besøgende 
                interagerer med siden.
              </p>
              <div className="bg-[#F4F2EE] p-4 text-sm text-[#1E2A24]/70 mb-4">
                <p><strong>Udbyder:</strong> Google LLC</p>
                <p><strong>Measurement ID:</strong> G-WH33T6S5KP</p>
                <p><strong>Cookies:</strong> _ga, _ga_*, _gid</p>
                <p><strong>Formål:</strong> Besøgsstatistik, brugeradfærd, sidevisninger</p>
                <p><strong>Udløb:</strong> Op til 2 år</p>
              </div>
              <p className="text-[#1E2A24]/70 leading-relaxed">
                Google Analytics-cookies sættes <strong>kun</strong>, hvis du aktivt vælger 
                "Acceptér alle" i vores cookiebanner. Vælger du "Kun nødvendige", indsamles 
                ingen analytics-data.
              </p>

              <h3 className="font-medium text-[#0E3D24] mt-6 mb-3">5.3 Google Consent Mode v2</h3>
              <p className="text-[#1E2A24]/70 leading-relaxed">
                Vi har implementeret Google Consent Mode v2, som sikrer, at Google Analytics 
                respekterer dit cookievalg. Før du træffer et valg, er alle analytics-funktioner 
                som standard deaktiveret (<code className="bg-[#F4F2EE] px-1">analytics_storage: denied</code>). 
                Først når du accepterer analytics-cookies, aktiveres tracking.
              </p>
            </div>

            {/* Consent Management */}
            <div className="mb-12">
              <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#0E3D24] mb-4">
                6. Ændring eller tilbagetrækning af samtykke
              </h2>
              <p className="text-[#1E2A24]/70 leading-relaxed mb-4">
                Du kan til enhver tid ændre eller trække dit cookiesamtykke tilbage ved at klikke 
                på <strong>"Cookie indstillinger"</strong> i bunden af hjemmesiden (footeren).
              </p>
              <p className="text-[#1E2A24]/70 leading-relaxed mb-4">
                Her kan du:
              </p>
              <ul className="list-disc pl-6 text-[#1E2A24]/70 space-y-2">
                <li>Se dit nuværende samtykkevalg</li>
                <li>Skifte fra "Kun nødvendige" til "Acceptér alle" (give samtykke)</li>
                <li>Skifte fra "Acceptér alle" til "Kun nødvendige" (trække samtykke tilbage)</li>
              </ul>
              <p className="text-[#1E2A24]/70 leading-relaxed mt-4">
                Når du trækker samtykke tilbage, stopper vi med at indsamle analytics-data, 
                men allerede indsamlede data slettes ikke automatisk hos Google.
              </p>
            </div>

            {/* Data Retention */}
            <div className="mb-12">
              <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#0E3D24] mb-4">
                7. Opbevaring af personoplysninger
              </h2>
              <p className="text-[#1E2A24]/70 leading-relaxed mb-4">
                Vi opbevarer dine personoplysninger så længe, det er nødvendigt for at opfylde 
                de formål, de blev indsamlet til:
              </p>
              <ul className="list-disc pl-6 text-[#1E2A24]/70 space-y-2">
                <li><strong>Kontaktformular-henvendelser:</strong> Opbevares i op til 2 år, medmindre der indledes et forretningssamarbejde</li>
                <li><strong>Google Analytics-data:</strong> Opbevares i op til 26 måneder (Googles standardindstilling)</li>
                <li><strong>Cookiesamtykke (localStorage):</strong> Opbevares indtil du manuelt sletter det eller ændrer dit valg</li>
              </ul>
            </div>

            {/* Data Sharing */}
            <div className="mb-12">
              <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#0E3D24] mb-4">
                8. Videregivelse af oplysninger
              </h2>
              <p className="text-[#1E2A24]/70 leading-relaxed mb-4">
                Vi videregiver ikke dine personoplysninger til tredjeparter, medmindre:
              </p>
              <ul className="list-disc pl-6 text-[#1E2A24]/70 space-y-2">
                <li>Det er nødvendigt for at levere vores ydelser (f.eks. leverandører)</li>
                <li>Vi er juridisk forpligtet til det</li>
                <li>Du har givet dit samtykke</li>
              </ul>
              <p className="text-[#1E2A24]/70 leading-relaxed mt-4">
                Google Analytics-data behandles af Google LLC. Google er Privacy Shield-certificeret 
                og overholder EU's standardkontraktbestemmelser for dataoverførsler.
              </p>
            </div>

            {/* Your Rights */}
            <div className="mb-12">
              <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#0E3D24] mb-4">
                9. Dine rettigheder efter GDPR
              </h2>
              <p className="text-[#1E2A24]/70 leading-relaxed mb-4">
                Du har følgende rettigheder i henhold til databeskyttelsesforordningen (GDPR):
              </p>
              <ul className="list-disc pl-6 text-[#1E2A24]/70 space-y-3">
                <li><strong>Ret til indsigt</strong> – Du kan anmode om at få bekræftet, om vi behandler personoplysninger om dig, og få adgang til disse oplysninger</li>
                <li><strong>Ret til berigtigelse</strong> – Du kan få rettet urigtige oplysninger om dig</li>
                <li><strong>Ret til sletning</strong> – Du kan i visse tilfælde få slettet oplysninger om dig</li>
                <li><strong>Ret til begrænsning</strong> – Du kan i visse tilfælde få begrænset behandlingen af dine oplysninger</li>
                <li><strong>Ret til dataportabilitet</strong> – Du kan i visse tilfælde få udleveret dine oplysninger i et struktureret, almindeligt anvendt format</li>
                <li><strong>Ret til indsigelse</strong> – Du kan gøre indsigelse mod vores behandling af dine oplysninger</li>
                <li><strong>Ret til at tilbagekalde samtykke</strong> – Hvis behandlingen er baseret på samtykke, kan du til enhver tid trække det tilbage</li>
              </ul>
              <p className="text-[#1E2A24]/70 leading-relaxed mt-4">
                For at udøve dine rettigheder, kontakt os venligst på{' '}
                <a href="mailto:info@dilling-el.dk" className="text-[#1F5A3A] hover:text-[#0E3D24]">
                  info@dilling-el.dk
                </a>.
              </p>
            </div>

            {/* Complaints */}
            <div className="mb-12">
              <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#0E3D24] mb-4">
                10. Klageadgang
              </h2>
              <p className="text-[#1E2A24]/70 leading-relaxed mb-4">
                Hvis du mener, at vores behandling af dine personoplysninger er i strid med 
                databeskyttelsesreglerne, kan du klage til:
              </p>
              <div className="bg-[#F4F2EE] p-6 text-[#1E2A24]/70">
                <p className="font-medium text-[#0E3D24]">Datatilsynet</p>
                <p>Carl Jacobsens Vej 35</p>
                <p>2500 Valby</p>
                <p className="mt-2">
                  Telefon: <a href="tel:+4533193200" className="text-[#1F5A3A] hover:text-[#0E3D24]">+45 33 19 32 00</a>
                </p>
                <p>
                  Email: <a href="mailto:dt@datatilsynet.dk" className="text-[#1F5A3A] hover:text-[#0E3D24]">dt@datatilsynet.dk</a>
                </p>
                <p>
                  Web: <a href="https://www.datatilsynet.dk" target="_blank" rel="noopener noreferrer" className="text-[#1F5A3A] hover:text-[#0E3D24]">www.datatilsynet.dk</a>
                </p>
              </div>
            </div>

            {/* Changes */}
            <div className="mb-12">
              <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#0E3D24] mb-4">
                11. Ændringer til denne politik
              </h2>
              <p className="text-[#1E2A24]/70 leading-relaxed">
                Vi forbeholder os ret til at opdatere denne privatlivs- og cookiepolitik. 
                Ved væsentlige ændringer vil vi informere dig via hjemmesiden. Vi anbefaler, 
                at du regelmæssigt gennemgår denne side for at holde dig opdateret.
              </p>
            </div>

            {/* Contact */}
            <div className="mb-12">
              <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#0E3D24] mb-4">
                12. Kontakt
              </h2>
              <p className="text-[#1E2A24]/70 leading-relaxed mb-4">
                Har du spørgsmål til denne politik eller vores behandling af personoplysninger, 
                er du velkommen til at kontakte os:
              </p>
              <div className="bg-[#F4F2EE] p-6 text-[#1E2A24]/70">
                <p className="font-medium text-[#0E3D24]">Dilling El ApS</p>
                <p>Gammel Darupvej 5D</p>
                <p>4000 Roskilde</p>
                <p className="mt-2">
                  Email: <a href="mailto:info@dilling-el.dk" className="text-[#1F5A3A] hover:text-[#0E3D24]">info@dilling-el.dk</a>
                </p>
                <p>
                  Telefon: <a href="tel:+4546461234" className="text-[#1F5A3A] hover:text-[#0E3D24]">+45 46 46 12 34</a>
                </p>
              </div>
            </div>

          </motion.div>
        </div>
      </section>
    </main>
  );
}
